import { useEffect, useState, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useView } from '../contexts/ViewContext';
import { Card, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShoppingCart, TrendingUp, Package, FileText, Loader2, Target, ClipboardList, PackageCheck, CalendarRange, AlarmClock } from 'lucide-react';
import { purchasingAPI, coreAPI, platformSettingsAPI, fetchAllPages, PurchaseOrder, CommodityGroup, CommodityType, Site, CompanyGroup } from '../lib/api';
import type { PurchasingTabsVisibility } from '../lib/api/types/common';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsCarrier } from '../hooks/useIsCarrier';
import MySuppliersToggle from '../components/purchasing/MySuppliersToggle';
import { useMySuppliersUrlState } from '../components/purchasing/useMySuppliersUrlState';
import { ackStatusFromUrl } from '../components/purchasing/tabs/poheaders/poHeaderFilterShared';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { getPortalLabels } from '../lib/portalLabels';
import { usePortalMode } from '../hooks/usePortalMode';
import { Cache } from '../lib/cache';
import PODetailModal from '../components/purchasing/PODetailModal';
import POHeadersTab from '../components/purchasing/tabs/POHeadersTab';
import { buildPOHeaderCountParams } from '../components/purchasing/tabs/po-headers/poHeaderCountParams';
import { erpOrderTypeOptions } from '../components/purchasing/tabs/po-lines/types';
import ContractPricingTab from '../components/purchasing/tabs/ContractPricingTab';
import { usePriceBookAccess } from '../hooks/usePriceBookAccess';
import POLinesTab from '../components/purchasing/tabs/POLinesTab';
import LineDeepLinkModal from '../components/purchasing/LineDeepLinkModal';
import ASNTab from '../components/purchasing/tabs/ASNTab';
import DeliveryTab from '../components/purchasing/tabs/DeliveryTab';
import ForecastTab from '../components/purchasing/tabs/ForecastTab';
import LateToRaiseTab from '../components/purchasing/tabs/late-to-raise/LateToRaiseTab';
import CallOffTab from '../components/purchasing/tabs/CallOffTab';
import POLineSchedules from './POLineSchedules';
import { usePagination, useModal } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';
import { useLogisticsCallOffRedirect } from '../components/call-off/logistics/useLogisticsCallOffRedirect';
import { resolvePoDeepLink } from './purchasing/resolvePoDeepLink';

// Lazy load RFQ page as tab
const RFQTab = lazy(() => import('./RFQ'));

// Currency symbol mapping for tab icon display
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    JPY: '¥',
    CNY: '¥',
    INR: '₹',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'Fr',
    MXN: '$',
  };
  return symbols[currency] || currency;
};

//: CR-7732: every tab the strip can render, in render order. The column
//: count and the triggers both read it, so a hidden tab leaves no gap.
const PURCHASING_TAB_IDS = [
  'rfq', 'contract_pricing', 'po_headers', 'po_lines', 'schedules',
  'forecast', 'late_to_raise', 'asn', 'deliveries', 'call_off',
];

export default function Purchasing() {
  const { preferences } = usePreferences();
  const { user } = useAuth();
  // Buyer-code scoping: default to "my suppliers" for users with codes;
  // ?all_suppliers=true opens on All suppliers (CR-7388 Classic links).
  const buyerCodes = user?.buyer_codes ?? [];
  const [mySuppliersOnly, setMySuppliersOnly] = useMySuppliersUrlState();
  const { isFeatureEnabled, isMenuItemVisible, isOpenOrdersControlHidden } = useMenuSettings();
  const { portalMode, isSupplier, isHelix } = usePortalMode();
  // CR-7732: a logistics company carries loads; it does not trade them.
  const isLogistics = user?.company_type === 'logistics';
  // CR-8613: its call-off links belong on Logistics Call-Offs.
  useLogisticsCallOffRedirect(isLogistics);
  // A supplier acting as a haulier keeps its own order screens — those orders
  // are real — but not the parts-planning ones, which it has no parts for.
  const isCarrier = useIsCarrier();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalLabels = getPortalLabels(portalMode);

  // Read URL parameters for deep linking
  const urlTab = searchParams.get('tab');
  const urlPo = searchParams.get('po');
  const urlPoId = searchParams.get('po_id');  // CR-7445: notifications link by database id
  const urlLine = searchParams.get('line');
  const urlSearch = searchParams.get('search');
  const urlLineTab = searchParams.get('lineTab');  // CR-3929: Auto-open specific tab in line detail
  const urlSupplier = searchParams.get('supplier');  // CR-5766: Supplier filter from Performance KPI click-through
  // CR-8600: a supplier's customer card (View Orders) opens PO Headers on that customer site, by id.
  const urlCustomer = /^\d+$/.test(searchParams.get('customer') ?? '') ? searchParams.get('customer') : null;
  const urlStatusTab = searchParams.get('status_tab');  // CR-5766: Status tab from Performance KPI click-through
  // CR-5563: KPI reporting window from the supplier Performance tab. PO Headers
  // already filters on issue_date (see the queryParams mapping below), which is
  // the same anchor the windowed total_orders / completed_orders counts use —
  // this only seeds that existing filter from the URL, as `supplier` already is.
  const urlDateFrom = searchParams.get('date_from');
  const urlDateTo = searchParams.get('date_to');

  const showSchedules = isFeatureEnabled('po_line_schedules');

  // Purchasing tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<PurchasingTabsVisibility>({
    rfq: true, pricing: true, po_headers: true, po_lines: true,
    forecast: true, asn: true, deliveries: true, call_off: true, late_to_raise: false,
  });
  // CR-5884: Forecast tab can be hidden from supplier viewers via the
  // Admin Setup → Purchasing → Open Orders "Hide Forecasting from Supplier"
  // toggle. Customer-side users always see Forecast.
  const hideForecastForSupplier = isOpenOrdersControlHidden('hide_forecasting_from_supplier', isSupplier);
  const forecastTabVisible = tabVisibility.forecast && !hideForecastForSupplier;
  // CR-6346: the Pricebooks tab is customer-only unless that customer has
  // deliberately shared its price books with this supplier. Resolved server-side
  // and fails closed, so an unreachable check never opens the tab.
  const { hasAccess: hasPriceBookAccess } = usePriceBookAccess(isSupplier);
  const pricingTabVisible = tabVisibility.pricing && (!isSupplier || hasPriceBookAccess);
  const [tabsReady, setTabsReady] = useState(false);

  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.purchasing_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.purchasing_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabsReady(true));
  }, []);

  // Call-Off visibility is gated by the per-supplier SupplierCustomerRelationship.call_off_enabled
  // flag, surfaced via isFeatureEnabled('switch_call_off_tab'). The tenant admin can still hide
  // it entirely via menu visibility / tab visibility.
  // A supplier a customer made its Logistics Provider gets the tab too — it
  // shows the carrier view there (CallOffTab).
  const showCallOff = tabVisibility.call_off && isMenuItemVisible('callOff')
    && (isFeatureEnabled('switch_call_off_tab') || isFeatureEnabled('logistics_view'));
  // CR-7058: the PO Lines toggle is now authoritative, like every other entry
  // in Navigation Menu Management. It previously read
  // `tabVisibility.po_lines || !showSchedules`, which made it a no-op on any
  // tenant without the Schedules surface — an admin switched it off and
  // nothing happened, on the page and via ?tab=po_lines alike. When it is off
  // the page falls back to the first tab that survives (see defaultTab), so a
  // tenant that hides PO Lines still has a working Purchasing page.
  const poLinesTabVisible = tabVisibility.po_lines;

  // CR-7058: one place that says whether a tab exists for this viewer, so a
  // ?tab=... deep link can never select a surface whose trigger is hidden.
  // Previously only 'schedules' was checked here, and ?tab=rfq rendered the
  // RFQ panel on a tenant that had switched the RFQ tab off in Platform Admin
  // → Navigation Menu Management → Purchasing.
  const isTabVisible = (tab: string): boolean => {
    // CR-7732: a haulier has no orders of its own — no PO lines, deliveries,
    // forecasts, stock planning or parts list. It comes to Purchasing for the
    // shipping notice it has to clear and the loads it is carrying, so those
    // are the only two tabs it is offered. Asked by the trigger strip as well
    // as the deep-link handler: they used to read the raw flags separately,
    // which is why this restriction hid the panels but left the tabs showing.
    // A haulier reaches its loads through the Logistics Call-Offs sidebar
    // item, which is the screen built for it; Purchasing is only where it
    // opens the shipping notice it has to clear.
    if (isLogistics) return tab === 'asn' && tabVisibility.asn;
    // CR-7732: no forecasting for a haulier of either kind — it moves other
    // people's parts and has none of its own to plan.
    if (isCarrier && tab === 'forecast') return false;
    switch (tab) {
      case 'rfq': return !isSupplier && tabVisibility.rfq;
      case 'contract_pricing': return pricingTabVisible;
      case 'po_headers': return tabVisibility.po_headers;
      case 'po_lines': return poLinesTabVisible;
      case 'schedules': return showSchedules;
      case 'forecast': return forecastTabVisible;
      // CR-8623: customer side only, and only where an admin switched it on.
      case 'late_to_raise': return !isSupplier && tabVisibility['late_to_raise'] === true;
      case 'asn': return tabVisibility.asn;
      case 'delivery':
      case 'deliveries': return tabVisibility.deliveries;
      case 'call_off': return showCallOff;
      // Unknown tab ids fall through to the default rather than rendering
      // nothing at all.
      default: return false;
    }
  };

  // Default tab:
  //  - when the Schedules surface is enabled (po_line_schedules / showSchedules),
  //    land on Schedules — for customers AND suppliers
  //  - otherwise fall back to PO Lines
  //  - an explicit ?tab=... in the URL wins, but only if that tab is visible
  // NOTE: this must key off the SAME flag that renders the Schedules tab
  // (showSchedules). Previously it used a separate `schedule_collaboration`
  // flag, so tenants with schedule lines enabled but collaboration off showed
  // the Schedules tab yet still defaulted to PO Lines.
  const defaultTab = (): string => {
    // CR-7732: through isTabVisible, not the raw flags. A haulier has neither
    // of these, and the early returns handed it 'po_lines' anyway — so the
    // page opened on another tenant's order lines under a one-tab strip.
    if (isTabVisible('schedules')) return 'schedules';
    if (isTabVisible('po_lines')) return 'po_lines';
    // Every standard tab hidden: fall back to the first one that survives so
    // the page still shows something the tenant has enabled.
    return ['po_headers', 'asn', 'delivery', 'forecast', 'call_off', 'contract_pricing', 'rfq']
      .find(isTabVisible) ?? 'po_lines';
  };

  const getValidTab = (tab: string | null): string => {
    if (!tab) return defaultTab();
    // URL deep-links can't escape the tab-visibility rules: a tab that is
    // switched off (or not offered to this role) falls back to the default
    // rather than rendering its surface without a trigger.
    if (!isTabVisible(tab)) return defaultTab();
    return tab;
  };

  const [mainTab, setMainTabState] = useState(getValidTab(urlTab));

  // Sync main tab when URL params change (e.g. notification click while already on page)
  // ALSO re-run when feature flags resolve: on hard-refresh with ?tab=schedules,
  // showSchedules is initially false (menu settings still loading) so
  // getValidTab downgrades to 'po_lines'. Once the flag flips true we must
  // honour the original URL tab.
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab) {
      // Explicit URL tab — honour it (re-evaluate once flags resolve).
      if (getValidTab(newTab) !== mainTab) setMainTabState(getValidTab(newTab));
    } else if (showSchedules && mainTab === 'po_lines') {
      // No URL tab = default landing. showSchedules resolves async (menu
      // settings load after mount), so the initial default can fall back to
      // po_lines before the flag is known. Once it flips true, upgrade the
      // default to schedules so users land on the Schedules tab.
      setMainTabState('schedules');
    } else if (tabsReady && !isTabVisible(mainTab)) {
      // CR-7058: tab visibility arrives after mount, so a tab chosen while the
      // settings were still at their permissive defaults must be re-checked
      // once they land.
      setMainTabState(defaultTab());
    }
    // tabsReady / tabVisibility are dependencies because the visibility rules
    // they feed decide whether the current tab survives.
  }, [searchParams, showSchedules, tabsReady, tabVisibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // CR-5766: Sync supplier and status_tab filters for PO Headers tab only.
  // PO Lines tab handles its own URL params in usePOLinesState hook.
  // This handles deep-linking from Performance KPI click-throughs to PO Headers.
  useEffect(() => {
    // Only process URL params for po_headers tab
    const urlTab = searchParams.get('tab');
    if (urlTab !== 'po_headers') return;

    const urlSupplierParam = searchParams.get('supplier');
    const urlStatusTabParam = searchParams.get('status_tab');
    // Daily-digest deep link: exact header status (e.g. status=sent for
    // "waiting connection"). Applied as the status filter, panel opened.
    const urlStatusParam = searchParams.get('status');
    // CR-936: the digest's "Open PO Headers with No PDF Attachment" link is
    // ?status_tab=open&pdf_attached=no - land on the population it counted.
    const urlPdfParam = searchParams.get('pdf_attached');
    const urlPdfAttached = urlPdfParam === 'yes' || urlPdfParam === 'no' ? urlPdfParam : null;
    // CR-7388: the Classic "PO Headers Not Acknowledged" link sets the
    // existing Ack Status filter (CR-6679) to the status its figure counts.
    const urlAckStatus = ackStatusFromUrl(searchParams.get('ack_status_summary'));

    // CR-5563: the Performance tab can send a date window with no other param.
    const urlDateFromParam = searchParams.get('date_from');
    const urlDateToParam = searchParams.get('date_to');
    if (!urlSupplierParam && !urlStatusTabParam && !urlStatusParam && !urlPdfAttached
        && !urlAckStatus && !urlDateFromParam && !urlDateToParam && !urlCustomer) return;

    // CR-8600: re-applied here for the same reason as the dates below.
    if (urlCustomer) {
      setFilters(prev => ({ ...prev, site: urlCustomer }));
      setShowFilters(true);
    }

    // CR-5563: restore the date window here, not only in the useState seed.
    // The CR-4580 unmount reset wipes every filter, and React 18 StrictMode
    // runs that cleanup once during dev startup — so the seeded values are
    // gone before the first fetch. `supplier` already survives because this
    // effect re-applies it below; the dates need the same treatment or the
    // list silently ignores the period the user clicked from.
    if (urlDateFromParam || urlDateToParam) {
      setFilters(prev => ({
        ...prev,
        dateFrom: urlDateFromParam || prev.dateFrom,
        dateTo: urlDateToParam || prev.dateTo,
      }));
    }

    // Sync status tab from URL first
    if (urlStatusTabParam) {
      setActiveTab(urlStatusTabParam);
    }
    if (urlStatusParam) {
      setFilters(prev => ({ ...prev, status: urlStatusParam }));
      setShowFilters(true);
    }
    if (urlPdfAttached) {
      setFilters(prev => ({ ...prev, pdfAttached: urlPdfAttached }));
      setShowFilters(true);
    }
    if (urlAckStatus) {
      setFilters(prev => ({ ...prev, ackStatus: urlAckStatus }));
      setShowFilters(true);
    }
    if (!urlSupplierParam) return;

    // CR-5766: Check if supplier param is numeric ID or name
    // If numeric, look up the supplier name for display in the ComboboxFilter
    // (POHeadersTab uses supplier names as filter values, not IDs)
    //
    // CR-5563: every branch below now goes through applySupplier, which also
    // bumps searchTrigger. The lookup is async, so a bump at the end of this
    // effect would fire BEFORE the supplier landed — the fetch effect would
    // hit its `!filters.supplier` guard, return, and nothing would re-run it.
    const applySupplier = (value: string) => {
      setFilters(prev => ({ ...prev, supplier: value }));
      setTextInputs(prev => ({ ...prev, supplier: value }));
    };

    const isNumericId = /^\d+$/.test(urlSupplierParam);
    if (isNumericId) {
      // CR-5563: apply the ID and fetch FIRST, resolve the display name after.
      //
      // fetchData branches on whether textInputs.supplier looks numeric: an id
      // becomes `supplier=<id>` (exact), a name becomes
      // `supplier__name__icontains`. Fetching only after the name had been
      // substituted turned this deep link into a fuzzy match, so a supplier
      // whose name is a prefix of another ("ACME ALLOYS" vs "ACME ALLOYS UK")
      // pulled in the other company's orders and the row count no longer
      // matched the KPI that was clicked.
      applySupplier(urlSupplierParam);
      setSearchTrigger(t => t + 1);

      // Display only — the combobox shows names, not ids (CR-5766). No refetch:
      // the exact-id query above is the one we want on screen.
      purchasingAPI.getLineFilterOptions().then(res => {
        const suppliers = res.data.suppliers || [];
        const supplier = suppliers.find((s: { id: number; name: string }) => String(s.id) === urlSupplierParam);
        if (supplier) {
          setFilters(prev => ({ ...prev, supplier: supplier.name }));
          setTextInputs(prev => ({ ...prev, supplier: supplier.name }));
        }
      }).catch(() => {
        // Keep the id on screen rather than clearing the filter.
      });
    } else {
      applySupplier(urlSupplierParam);
      setSearchTrigger(t => t + 1);
    }

    // Expand filter panel so user can see the applied filter
    setShowFilters(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only


  // Wrapper to enforce tab restrictions and persist tab in URL
  const setMainTab = (tab: string) => {
    const validTab = getValidTab(tab);
    setMainTabState(validTab);
    const next = new URLSearchParams(searchParams);
    // CR-7388: status_tab names a pill of the tab the link opened (PO Lines
    // and PO Headers both read it), so it is dropped on a switch rather than
    // selecting a pill on the next tab the link never meant.
    if (validTab !== mainTab) next.delete('status_tab');
    next.set('tab', validTab);
    setSearchParams(next, { replace: true });
  };
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // CR-5766: Use status_tab from URL if provided (from Performance KPI click-through)
  // CR-7388: only a PO Headers link's status_tab is a PO Headers pill; a PO
  // Lines link's (needs_action, otif_review) matched no pill here.
  const [activeTab, setActiveTab] = useState((urlTab === 'po_headers' && urlStatusTab) || 'open');
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [openOrderValue, setOpenOrderValue] = useState<number>(0);
  // CR-7714 FE-13: the currency the stats endpoint returned the value in.
  const [openOrderValueCurrency, setOpenOrderValueCurrency] = useState<string | null>(null);
  const [openOrderValueUnscoped, setOpenOrderValueUnscoped] = useState<boolean>(false);
  // CR-5766: Expand filter panel when supplier is in URL (from Performance KPI click-through)
  // CR-5563: also open when a date window arrived, so the applied period is visible.
  const [showFilters, setShowFilters] = useState(Boolean(urlSupplier || urlDateFrom || urlDateTo || urlCustomer));
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [poSortField, setPOSortField] = useState('-issue_date');
  // CR-6446: searchTrigger controls when to fetch data (like PO Lines pattern).
  // Filter changes update state immediately for UI responsiveness, but data
  // only fetches when "Apply Filters" is clicked (incrementing this counter).
  const [searchTrigger, setSearchTrigger] = useState(0);
  const pagination = usePagination({ pageSize: 50 });
  const detailModal = useModal<number>();
  // CR-7716: which tab the PO detail modal opens on (a PDF/Cert click → Documents).
  const [detailInitialTab, setDetailInitialTab] = useState('overview');
  const openPODetail = (poId: number, tab: string = 'overview') => {
    setDetailInitialTab(tab);
    detailModal.open(poId);
  };

  // Text input states (for debouncing) - CR-3954: Initialize from URL search param
  // CR-5766: Also seed supplier from URL param for Performance KPI click-through
  const [textInputs, setTextInputs] = useState({
    poNumber: urlSearch || '',
    supplier: urlSupplier || '',
    customer: '',
  });

  // CR-4580: Reset PO Header filters on unmount so they don't persist
  useEffect(() => {
    return () => {
      setFilters({ poNumber: '', supplier: '', customer: '', status: '', dateFrom: '', dateTo: '', site: '', warehouse: '', division: '', companyCountry: '', supplierGroup: '', supplierCountry: '', supplierCode: '', supplierType: '', commodityGroup: '', commodityType: '', orderType: '', pdfAttached: '', strategic: '', ackStatus: '' });
      setTextInputs({ poNumber: '', supplier: '', customer: '' });
    };
  }, []);

  // Actual filter state (used for API calls)
  // CR-5766: Seed supplier filter from URL param for Performance KPI click-through
  // CR-6439: Added division, companyCountry, supplierCountry for cascade filtering
  const [filters, setFilters] = useState({
    poNumber: urlSearch || '',
    supplier: urlSupplier || '',
    customer: '',
    status: '',
    dateFrom: urlDateFrom || '',   // CR-5563: Performance tab drill-through
    dateTo: urlDateTo || '',       // CR-5563: Performance tab drill-through
    site: urlCustomer || '',  // CR-8600: the customer card's View Orders
    warehouse: '',  // CR-6446: Warehouse filter for Site → Warehouse cascade
    division: '',
    companyCountry: '',
    supplierGroup: '',
    supplierCountry: '',
    supplierCode: '',   // CR-6439 rework: Supplier section — Code (contains match)
    supplierType: '',   // CR-6439 rework: Classification — internal / external
    commodityGroup: '',
    commodityType: '',
    orderType: '',      // CR-6439 rework: Classification — ERP order type code
    pdfAttached: '',
    strategic: '',
    ackStatus: '',  // CR-6679: Header-level acknowledgement status filter
  });

  // Filter dropdown options
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);
  // CR-6439: Store raw commodity types with commodity_group relationship for cascade filtering
  const [rawCommodityTypes, setRawCommodityTypes] = useState<Array<{ id: number; name: string; commodity_group: number }>>([]);
  // CR-6439: Additional filter options for cascade filtering (Division, Country filters)
  const [divisionOptions, setDivisionOptions] = useState<{ value: string; label: string }[]>([]);
  const [siteCountries, setSiteCountries] = useState<string[]>([]);
  const [supplierCountries, setSupplierCountries] = useState<string[]>([]);
  // CR-6439 rework: Order Type options — the ERP order-type codes present in scope.
  const [orderTypeOptions, setOrderTypeOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    setInitialLoadComplete(true);

    // Check cache for filter options (stable reference data, cached 15 min)
    // viewKey: the supplier-group options follow the site switcher.
    const FILTER_OPTS_CACHE_KEY = `purchasing_header_filter_options_v4_${viewKey}`; // CR-6439 rework: bumped for orderTypes
    const cached = Cache.get<{
      sites: { value: string; label: string }[];
      commodityGroups: { value: string; label: string }[];
      commodityTypes: { value: string; label: string }[];
      supplierGroups: { value: string; label: string }[];
      // CR-6439: Raw commodity types with commodity_group relationship
      rawCommodityTypes: Array<{ id: number; name: string; commodity_group: number }>;
      // CR-6439: Additional options for cascade filtering
      divisions: { value: string; label: string }[];
      siteCountries: string[];
      supplierCountries: string[];
      orderTypes: { value: string; label: string }[];
    }>(FILTER_OPTS_CACHE_KEY);

    if (cached) {
      setSiteOptions(cached.sites || []);
      setCommodityGroupOptions(cached.commodityGroups || []);
      setCommodityTypeOptions(cached.commodityTypes || []);
      setSupplierGroupOptions(cached.supplierGroups || []);
      setRawCommodityTypes(cached.rawCommodityTypes || []);
      setDivisionOptions(cached.divisions || []);
      setSiteCountries(cached.siteCountries || []);
      setSupplierCountries(cached.supplierCountries || []);
      setOrderTypeOptions(cached.orderTypes || []);
      return;
    }

    // Fetch filter options for PO Headers
    // CR-6439: Also fetch line filter options for divisions, site_countries, supplier_countries
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      // Supplier groups are a customer-side filter; the endpoint 403s for
      // supplier users, so don't ask.
      // The unfiltered list is Helix-only (403 for everyone else) — customer
      // users read their traded suppliers' groups from the scoped for-filter
      // endpoint instead of asking and swallowing the 403.
      isSupplier
        ? Promise.resolve([] as CompanyGroup[])
        : isHelix
          ? fetchAllPages<CompanyGroup>((page) => coreAPI.getCompanyGroups({ page_size: 100, page, company_type: 'supplier' })).catch(() => [] as CompanyGroup[])
          : coreAPI.getSupplierGroupsForFilter().then((res) => res.data.results || []).catch(() => [] as CompanyGroup[]),
      // CR-6439: Get divisions, site_countries, supplier_countries from line filter options
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: { divisions: [], site_countries: [], supplier_countries: [], commodity_groups: [], commodity_types: [], suppliers: [], customers: [], warehouses: [], erp_order_types: [] } })),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroups, lineFilterRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      const siteOpts = sites.map((s) => ({ value: String(s.id), label: s.name }));
      setSiteOptions(siteOpts);

      // CR-6446: For suppliers, use commodity data from PO Lines (getLineFilterOptions)
      // since suppliers don't own commodity groups - they use groups from their customers' PO Lines
      const lineFilterData = lineFilterRes.data;

      let groupOpts: { value: string; label: string }[];
      let typeOpts: { value: string; label: string }[];
      let rawTypes: Array<{ id: number; name: string; commodity_group: number }>;

      if (isSupplier && lineFilterData.commodity_groups?.length > 0) {
        // Supplier: use commodity groups/types from PO Lines
        groupOpts = (lineFilterData.commodity_groups || []).map((g: { id: number; name: string }) => ({
          value: String(g.id),
          label: g.name,
        }));
        typeOpts = (lineFilterData.commodity_types || []).map((t: { id: number; name: string }) => ({
          value: String(t.id),
          label: t.name,
        }));
        rawTypes = (lineFilterData.commodity_types || []).map((t: { id: number; name: string; commodity_group_id: number }) => ({
          id: t.id,
          name: t.name,
          commodity_group: t.commodity_group_id,
        }));
      } else {
        // Customer/Helix: use owned commodity groups/types from core API
        const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
        groupOpts = groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name }));

        const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
        const enabledTypes = types.filter((t: CommodityType) => t.enabled);
        typeOpts = enabledTypes.map((t: CommodityType) => ({ value: String(t.id), label: t.name }));

        rawTypes = enabledTypes.map((t: CommodityType) => ({
          id: t.id,
          name: t.name,
          commodity_group: t.commodity_group,
        }));
      }

      setCommodityGroupOptions(groupOpts);
      setCommodityTypeOptions(typeOpts);
      setRawCommodityTypes(rawTypes);

      const supplierGroupOpts = companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name }));
      setSupplierGroupOptions(supplierGroupOpts);

      // CR-6439: Extract divisions, site_countries, supplier_countries from line filter options
      // Note: lineFilterData already declared above for CR-6446 supplier commodity fix
      const divisionOpts = (lineFilterData.divisions || []).map((d: { id: number; name: string }) => ({
        value: String(d.id),
        label: d.name,
      }));
      setDivisionOptions(divisionOpts);
      setSiteCountries(lineFilterData.site_countries || []);
      setSupplierCountries(lineFilterData.supplier_countries || []);
      // CR-6439 rework: Order Type = the ERP order-type codes present in scope,
      // labelled the same way as the PO Lines panel.
      const orderTypeOpts = erpOrderTypeOptions(lineFilterData.erp_order_types);
      setOrderTypeOptions(orderTypeOpts);

      // Cache for 15 minutes (reference data changes rarely)
      Cache.set(FILTER_OPTS_CACHE_KEY, {
        sites: siteOpts,
        commodityGroups: groupOpts,
        commodityTypes: typeOpts,
        supplierGroups: supplierGroupOpts,
        rawCommodityTypes: rawTypes,
        divisions: divisionOpts,
        siteCountries: lineFilterData.site_countries || [],
        supplierCountries: lineFilterData.supplier_countries || [],
        orderTypes: orderTypeOpts,
      }, 15 * 60 * 1000);
    });
  }, [viewKey]);

  // CR-3929/CR-3968: Auto-open PO detail or PO line from notification
  useEffect(() => {
    if (urlLine) {
      // Deep link to specific PO line - switch to po_lines tab
      setMainTab('po_lines');
    } else if (urlPo || urlPoId) {
      // CR-7445: ?po_id= by id, ?po= by number (legacy ?po=<id> still opens).
      resolvePoDeepLink({ poId: urlPoId, po: urlPo }).then((id) => {
        if (id !== null) openPODetail(id);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPo, urlPoId, urlLine]);

  // Text inputs are applied on explicit Search button click only
  // (no auto-debounce — avoids jittery re-renders while typing)

  // CR-6446: Fetch data only on explicit triggers (page change, tab change, search button click),
  // NOT on every filter value change. This prevents "jerky" re-renders while adjusting filters.
  // The pattern matches PO Lines (usePOLinesState) which uses searchTrigger.
  useEffect(() => {
    if (viewContextLoading) return;
    // Only fetch PO headers when the PO Headers tab is active.
    // Other tabs (PO Lines, RFQ, etc.) manage their own data fetching.
    if (initialLoadComplete && mainTab === 'po_headers') {
      // CR-5766: Wait for URL params to sync to filter state before fetching.
      // If URL has supplier param but filters.supplier is empty, the sync hasn't happened yet.
      const urlSupplierParam = searchParams.get('supplier');
      if (urlSupplierParam && !filters.supplier) {
        return; // Wait for URL param sync useEffect to set the filter
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, pagination.currentPage, searchTrigger, activeTab, initialLoadComplete, mainTab, portalMode, viewKey, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, poSortField, mySuppliersOnly]);

  // CR-7716: one builder for the PO list cache key. handleSearch rebuilt it by
  // hand and had drifted (no mySuppliersOnly, page fixed) so its clear could miss.
  const ordersCacheKey = (page: number) =>
    `purchasing_${activeTab}_${page}_${poSortField}_${viewKey}_${mySuppliersOnly}_${JSON.stringify({ ...filters, ...textInputs })}`;

  // `force` skips the cached copy: after an upload (or Refresh) the list must
  // come from the server, or the new document stays invisible (CR-7716).
  const fetchData = async ({ force = false }: { force?: boolean } = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters based on filters and active tab
      const queryParams: Record<string, string | number | boolean> = {
        ordering: poSortField,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
      };
      // Buyer-code scope override (backend defaults to "my suppliers").
      if (buyerCodes.length > 0 && !mySuppliersOnly) queryParams['all_suppliers'] = true;

      // Create a cache key based on current filters and pagination.
      // CR-6219: include the active view context (viewKey = level-group-company-site)
      // so switching the top-bar view refetches instead of serving the prior
      // view's cached orders (the page re-ran fetchData but the key collided).
      // CR-6446: include textInputs since fetchData now reads from them directly.
      const cacheKey = ordersCacheKey(pagination.currentPage);

      // Check cache first
      const cachedData = force ? null : Cache.get<{ orders: PurchaseOrder[]; totalCount: number }>(cacheKey);
      if (cachedData) {
        setOrders(cachedData.orders);
        pagination.setFromResponse(cachedData.totalCount);
        setLoading(false);
        return;
      }

      // CR-6446: Read text inputs directly from textInputs state (not filters).
      // This allows handleSearch to avoid calling setFilters, preventing filter
      // section re-renders when "Apply Filters" is clicked.
      if (textInputs.poNumber) {
        queryParams['po_number__icontains'] = textInputs.poNumber;
      }

      // CR-5766: Use textInputs state (URL params are synced to state on mount)
      if (textInputs.supplier) {
        // Check if supplier is a numeric ID or a name string
        // If numeric, use exact ID match; if name, use icontains lookup
        const isNumericId = /^\d+$/.test(textInputs.supplier);
        if (isNumericId) {
          queryParams['supplier'] = textInputs.supplier;
        } else {
          queryParams['supplier__name__icontains'] = textInputs.supplier;
        }
      }

      if (textInputs.customer) {
        queryParams['customer__name__icontains'] = textInputs.customer;
      }

      // Apply status filter from dropdown or active tab
      const effectiveTab = activeTab || 'open';
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (effectiveTab !== 'all') {
        // Tab-based filtering. CR-936 rework: Open / Closed go by the order's
        // lines (open while any line is open), not the stored header status.
        if (effectiveTab === 'open') {
          queryParams['header_state'] = 'open';
        } else if (effectiveTab === 'delivered') {
          queryParams['status'] = 'delivered';
        } else if (effectiveTab === 'closed') {
          queryParams['header_state'] = 'closed';
        }
        // Note: 'on_hold' tab would need backend support for filtering by line.on_hold
      }

      // Apply date filters
      if (filters.dateFrom) {
        queryParams['issue_date__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['issue_date__lte'] = filters.dateTo;
      }

      // New PO Header filters
      if (filters.site) {
        queryParams['customer'] = filters.site;
      }
      // CR-6446: Division, Company Country and Warehouse narrowed their
      // dropdowns but were never sent, so applying one alone changed nothing.
      // Division is the customer's group; the warehouse is the PO's delivery
      // warehouse.
      if (filters.division) {
        queryParams['customer__group'] = filters.division;
      }
      if (filters.companyCountry) {
        queryParams['customer__country'] = filters.companyCountry;
      }
      if (filters.warehouse) {
        queryParams['delivery_warehouse'] = filters.warehouse;
      }
      if (filters.supplierGroup) {
        queryParams['supplier__group'] = filters.supplierGroup;
      }
      // Supplier Country. Rendered since CR-6446 but never sent, so choosing
      // a country left the grid on the full set. Same path PO Lines, ASN and
      // Deliveries filter on.
      if (filters.supplierCountry) {
        queryParams['supplier__country'] = filters.supplierCountry;
      }
      // CR-6439 rework: Supplier Code, Supplier Type and Order Type — the
      // controls client testing found missing from the customer panel.
      if (filters.supplierCode) {
        queryParams['supplier__supplier_code__icontains'] = filters.supplierCode;
      }
      if (filters.supplierType) {
        queryParams['supplier_type'] = filters.supplierType;
      }
      if (filters.orderType) {
        queryParams['erp_order_type'] = filters.orderType;
      }
      if (filters.commodityGroup) {
        queryParams['lines__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['lines__commodity_type'] = filters.commodityType;
      }
      if (filters.strategic === 'yes') {
        queryParams['supplier__supplier_profile__is_preferred'] = true;
      }
      // CR-6679: Header-level acknowledgement status filter
      if (filters.ackStatus) {
        queryParams['ack_status_summary'] = filters.ackStatus;
      }
      // CR-936: PDF version attached (yes/no). Deliberately shapes the tab
      // counts too, so the Open badge is the digest's "no PDF" number.
      if (filters.pdfAttached === 'yes' || filters.pdfAttached === 'no') {
        queryParams['pdf_attached'] = filters.pdfAttached;
      }

      // Spread view context params (group/company/site) for multi-customer support
      const viewParams = getViewQueryParams();
      Object.entries(viewParams).forEach(([key, value]) => {
        if (value !== undefined) queryParams[key] = value;
      });

      // Fetch orders and tab counts in parallel.
      // Tab counts must respect the user-applied filters (supplier, supplier_group,
      // commodity, date range, etc.) but NOT the status filter — each tab supplies
      // its own status. Strip status/status__in from the filter set before reuse.
      // The count probes must never inherit the list's page number: on page 2
      // of a tab whose sibling tab has a single order, ``?page=2&page_size=1``
      // 404s ("Invalid page") and used to blank the whole list.
      const countParams = buildPOHeaderCountParams(queryParams);
      // A failed count probe degrades to "no badge", never to a list error.
      const countOf = (params: Record<string, string | number | boolean>) =>
        purchasingAPI.getOrders(params).then((res) => res.data.count as number).catch(() => undefined);
      // Stats endpoint accepts a subset of filters (date_from/date_to/supplier/
      // customer/company/group). Map what we can; backend ignores keys it
      // doesn't know.
      const statsParams: Record<string, string | number | boolean> = {};
      Object.entries(viewParams).forEach(([k, v]) => { if (v !== undefined) statsParams[k] = v; });
      if (filters.dateFrom) statsParams['date_from'] = filters.dateFrom;
      if (filters.dateTo) statsParams['date_to'] = filters.dateTo;
      if (filters.site) statsParams['customer'] = filters.site;
      // CR-7232: read textInputs.supplier, the one slot the toolbar box, the
      // panel select and the grid fetch all share. This read filters.supplier,
      // which only the ?supplier= deep link ever set — so the Open Order Value
      // above the grid was computed against a different filter than the grid.
      if (textInputs.supplier) {
        const isNumericId = /^\d+$/.test(textInputs.supplier);
        if (isNumericId) {
          statsParams['supplier'] = textInputs.supplier;
        } else {
          statsParams['supplier__name__icontains'] = textInputs.supplier;
        }
      }
      if (filters.supplierGroup) statsParams['supplier__group'] = filters.supplierGroup;
      if (filters.commodityGroup) statsParams['lines__commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) statsParams['lines__commodity_type'] = filters.commodityType;
      const [ordersResponse, allCount, openCount, closedCount, statsRes] = await Promise.all([
        purchasingAPI.getOrders(queryParams),
        countOf(countParams),
        countOf({ ...countParams, header_state: 'open' }),
        countOf({ ...countParams, header_state: 'closed' }),
        // PO Headers reads exactly one number out of this response
        // (open_order_value, below). Asking for the full payload also built a
        // 12-week cost chart, a 12-month cost chart, an NCR breakdown, group
        // KPIs and supplier rankings that nothing on this page renders — 95
        // queries and ~3s cold, inside a Promise.all the whole page waits on.
        // sections=none keeps the base metrics and skips all of it: 54
        // queries, ~0.5s, same open_order_value.
        purchasingAPI.getDashboardStats({ ...statsParams, sections: 'none' }).catch(() => null),
      ]);

      setOrders(ordersResponse.data.results);
      pagination.setFromResponse(ordersResponse.data.count, (ordersResponse.data.results || []).length);
      const nextCounts: Record<string, number> = {};
      if (allCount !== undefined) nextCounts['all'] = allCount;
      if (openCount !== undefined) nextCounts['open'] = openCount;
      if (closedCount !== undefined) nextCounts['closed'] = closedCount;
      setTabCounts(nextCounts);
      setOpenOrderValue(statsRes?.data.open_order_value || 0);
      setOpenOrderValueCurrency(statsRes?.data.currency ?? null);
      // The stats endpoint honours only date / site / supplier / supplier-group
      // / commodity. Every other panel control leaves the figure wider than the
      // list, so the header has to say so. This listed three of the twelve, so
      // filtering by e.g. Order Type or Ack Status quietly showed a total that
      // did not match the rows underneath it.
      setOpenOrderValueUnscoped(
        Boolean(
          textInputs.poNumber ||
          textInputs.customer ||
          filters.strategic === 'yes' ||
          filters.supplierCountry ||
          filters.supplierCode ||
          filters.supplierType ||
          filters.orderType ||
          filters.pdfAttached ||
          filters.ackStatus ||
          filters.division ||
          filters.companyCountry ||
          filters.warehouse
        )
      );

      // Cache the data for 2 minutes (since purchasing data changes more frequently than dashboard)
      Cache.set(cacheKey, {
        orders: ordersResponse.data.results,
        totalCount: ordersResponse.data.count,
      }, 2 * 60 * 1000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load purchasing data'));
    } finally {
      setLoading(false);
    }
  };

  // CR-6446: clearFilters resets all filters AND triggers a data refresh
  const clearFilters = () => {
    setTextInputs({
      poNumber: '',
      supplier: '',
      customer: '',
    });
    setFilters({
      poNumber: '',
      supplier: '',
      customer: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      warehouse: '',  // CR-6446
      division: '',
      companyCountry: '',
      supplierGroup: '',
      supplierCountry: '',
      supplierCode: '',   // CR-6439 rework
      supplierType: '',   // CR-6439 rework
      commodityGroup: '',
      commodityType: '',
      orderType: '',      // CR-6439 rework
      pdfAttached: '',
      strategic: '',
      ackStatus: '',  // CR-6679
    });
    pagination.reset();
    // CR-5766: Clear URL params so a page refresh doesn't reapply old filters
    // (same pattern as Quality.tsx). CR-7388: the supplier scope is not a
    // filter and stays, so a refresh keeps the All suppliers choice.
    const kept = new URLSearchParams();
    const tab = searchParams.get('tab');
    if (tab) kept.set('tab', tab);
    if (searchParams.get('all_suppliers') === 'true') kept.set('all_suppliers', 'true');
    setSearchParams(kept, { replace: true });
    // CR-6446: Trigger data refresh with cleared filters
    setSearchTrigger(prev => prev + 1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      pagination.setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // CR-6446: handleSearch is called when "Apply Filters" is clicked.
  // It only resets pagination and increments searchTrigger to trigger fetch.
  // fetchData reads text inputs directly from textInputs state, so we don't
  // need to call setFilters here. This prevents filter section re-renders.
  // Pattern matches SO Lines (usePOLinesState.ts handleSearch).
  const handleSearch = () => {
    pagination.reset();
    // Invalidate cache and trigger fetch via searchTrigger.
    // CR-6219: keep this key in sync with the fetch key (includes viewKey + textInputs).
    Cache.remove(ordersCacheKey(1));
    // Increment searchTrigger to trigger the useEffect that fetches data
    setSearchTrigger(prev => prev + 1);
  };

  /**
   * Apply a single filter change.
   *
   * CR-6446: Filter changes update state immediately for UI responsiveness,
   * but do NOT trigger data fetch. User must click "Apply Filters" to fetch.
   * This prevents "jerky" re-renders while adjusting multiple filters.
   *
   * Uses functional updaters throughout: the date-range quick-period chips call
   * this twice in one tick (once for dateFrom, once for dateTo), and spreading
   * the captured `filters` object would make the second call overwrite the
   * first with a stale copy — the range would end up with only its "To" set.
   */
  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state (will be debounced)
    if (['poNumber', 'supplier', 'customer'].includes(key)) {
      setTextInputs((prev) => ({ ...prev, [key]: value }));
    } else {
      // CR-6439: Cascade resets (commodityGroup→commodityType, supplierGroup→supplier)
      // are now handled by usePOHeaderFilterCascades hook in POHeaderFilterSections.
      // This function just updates the filter state; the hook calls onFilterChange
      // again to clear dependent filters when needed.
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
    // CR-6446: Don't reset pagination here - let handleSearch do it when user clicks "Apply Filters"
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    pagination.reset(); // Reset to first page when changing tabs
  };

  const handlePONumberClick = (poNumber: string) => {
    // CR-5554: only call setSearchParams here — calling setMainTab as well
    // schedules a SECOND setSearchParams from setMainTab's wrapper which
    // reads the STALE searchParams (still without poFilter) and overwrites
    // back to just `?tab=po_lines`, dropping the filter we just added.
    // The useEffect on line ~106 already syncs mainTab from the URL, so
    // setting searchParams alone is sufficient.
    setSearchParams({ tab: 'po_lines', poFilter: poNumber }, { replace: true });
  };

  // Render a loading placeholder for the PO Headers content area (shown inside the tab layout,
  // NOT as a full-page replacement, so the tab bar stays visible and doesn't flicker).
  // CR-5766: Match Quality.tsx behavior - only show full-page loader when loading AND no data
  // exists yet. This prevents the filter section and data table from re-rendering/flashing
  // when clearing filters or refreshing while data is already displayed.
  const renderPOHeadersContent = () => {
    if (loading && orders.length === 0) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading purchase orders...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={() => fetchData({ force: true })}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
      {buyerCodes.length > 0 && (
        <div className="flex justify-end mb-2">
          <MySuppliersToggle myOnly={mySuppliersOnly} onChange={(v) => { setMySuppliersOnly(v); pagination.goToPage(1); }} codes={buyerCodes} />
        </div>
      )}
      <POHeadersTab
        orders={orders}
        loading={loading}
        totalCount={pagination.totalCount}
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
        onRowClick={(poId) => openPODetail(poId)}
        onDocumentClick={(poId) => openPODetail(poId, 'documents')}
        onPONumberClick={handlePONumberClick}
        textInputs={textInputs}
        filters={filters}
        siteOptions={siteOptions}
        supplierGroupOptions={supplierGroupOptions}
        commodityGroupOptions={commodityGroupOptions}
        commodityTypeOptions={commodityTypeOptions}
        rawCommodityTypes={rawCommodityTypes}
        divisionOptions={divisionOptions}
        siteCountries={siteCountries}
        supplierCountries={supplierCountries}
        orderTypeOptions={orderTypeOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        showFilters={showFilters}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onRefresh={() => fetchData({ force: true })}
        onSearch={handleSearch}
        onToggleFilters={() => setShowFilters(!showFilters)}
        tabCounts={tabCounts}
        openOrderValue={openOrderValue}
        openOrderValueCurrency={openOrderValueCurrency}
        openOrderValueUnscoped={openOrderValueUnscoped}
        sortField={poSortField}
        sortOrder={poSortField.startsWith('-') ? 'desc' : 'asc'}
        exportAllSuppliers={buyerCodes.length > 0 ? !mySuppliersOnly : undefined}
        isSupplier={isSupplier}
        onSort={(field) => {
          setPOSortField(prev => {
            if (prev === field) return `-${field}`;
            if (prev === `-${field}`) return field;
            return `-${field}`;
          });
          pagination.goToPage(1);
        }}
      />
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{portalLabels.ordersPageTitle}</h1>
          <p className="text-muted-foreground mt-2">
            {isSupplier
              ? 'Manage sales orders and track customer deliveries.'
              : 'Manage purchase orders and track procurement activities.'}
          </p>
        </div>
        {/* Filters button moved inside POHeadersTab for consistency with PO Lines */}
      </div>

      {/* KPI Cards - Hidden for now */}
      {/* <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total POs</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_pos || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.total_lines || 0} lines total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.total_spend || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Total procurement value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Orders</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.open_order_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Pending/In progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Arrears</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              £{((stats?.arrears_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Overdue orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PPM</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.ppm || 0) > 1500 ? 'text-red-600' : 'text-green-600'}`}>
              {(stats?.ppm || 0).toFixed(0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 1,500 PPM
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTIF</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={'text-2xl font-bold ' + (stats?.otif == null ? 'text-muted-foreground' : 'text-foreground')}>
              {stats?.otif == null ? 'N/A' : `${stats.otif.toFixed(1)}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              Target set in Admin Setup
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTD</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.otd || 0) >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats?.otd ? `${stats.otd.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 90%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">NCRs</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.ncrs || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Non-conformance reports
            </p>
          </CardContent>
        </Card>
      </div> */}

      {/* Main Navigation Tabs */}
      {/* RFQ and Forecast tabs are customer-only (customers create RFQs and share forecasts with suppliers) */}
      <Card className="border-2 shadow-sm">
        <CardHeader className="pb-3">
          {!tabsReady ? (
            /* Skeleton placeholder while tab visibility settings load — prevents tab bar flicker */
            <div className="flex gap-2 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-1 h-14 bg-slate-100 rounded-md" />
              ))}
            </div>
          ) : (
          <Tabs value={mainTab} onValueChange={setMainTab}>
            {/* CR-7732: the column count comes from the same isTabVisible the
                triggers below ask, not from the raw flags. They had drifted —
                a tab hidden from a haulier still reserved its column, leaving
                a blank slot on the end of the strip. Counting what actually
                renders is the only way the two cannot disagree. */}
            <TabsList className={`grid w-full h-auto bg-gradient-to-r from-slate-50 to-slate-100`} style={{ gridTemplateColumns: `repeat(${
              PURCHASING_TAB_IDS.filter(isTabVisible).length
            }, minmax(0, 1fr))` }}>
              {/* RFQ tab - Customer only, respects tab visibility */}
              {isTabVisible('rfq') && (
                <TabsTrigger value="rfq" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <ClipboardList className={`h-5 w-5 ${mainTab === 'rfq' ? 'text-cyan-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'rfq' ? 'text-cyan-700' : 'text-slate-600'}`}>RFQ</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('contract_pricing') && (
                <TabsTrigger value="contract_pricing" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <span className={`h-5 w-5 flex items-center justify-center text-lg font-semibold ${mainTab === 'contract_pricing' ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {getCurrencySymbol(preferences.currency)}
                    </span>
                    <span className={`text-xs font-medium ${mainTab === 'contract_pricing' ? 'text-emerald-700' : 'text-slate-600'}`}>Pricebooks</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('po_headers') && (
                <TabsTrigger value="po_headers" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <ShoppingCart className={`h-5 w-5 ${mainTab === 'po_headers' ? 'text-blue-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_headers' ? 'text-blue-700' : 'text-slate-600'}`}>{isSupplier ? 'SO Headers' : 'PO Headers'}</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('po_lines') && (
                <TabsTrigger value="po_lines" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <Package className={`h-5 w-5 ${mainTab === 'po_lines' ? 'text-purple-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_lines' ? 'text-purple-700' : 'text-slate-600'}`}>{isSupplier ? 'SO Lines' : 'PO Lines'}</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('schedules') && (
                <TabsTrigger value="schedules" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <CalendarRange className={`h-5 w-5 ${mainTab === 'schedules' ? 'text-cyan-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'schedules' ? 'text-cyan-700' : 'text-slate-600'}`}>Schedules</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('forecast') && (
                <TabsTrigger value="forecast" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <TrendingUp className={`h-5 w-5 ${mainTab === 'forecast' ? 'text-indigo-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'forecast' ? 'text-indigo-700' : 'text-slate-600'}`}>Forecast</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('late_to_raise') && (
                <TabsTrigger value="late_to_raise" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <AlarmClock className={`h-5 w-5 ${mainTab === 'late_to_raise' ? 'text-red-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'late_to_raise' ? 'text-red-700' : 'text-slate-600'}`}>Late to Raise</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('asn') && (
                <TabsTrigger value="asn" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <FileText className={`h-5 w-5 ${mainTab === 'asn' ? 'text-amber-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'asn' ? 'text-amber-700' : 'text-slate-600'}`}>ASN</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('deliveries') && (
                <TabsTrigger value="delivery" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <Target className={`h-5 w-5 ${mainTab === 'delivery' ? 'text-rose-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'delivery' ? 'text-rose-700' : 'text-slate-600'}`}>Deliveries</span>
                  </div>
                </TabsTrigger>
              )}
              {isTabVisible('call_off') && (
                <TabsTrigger value="call_off" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <PackageCheck className={`h-5 w-5 ${mainTab === 'call_off' ? 'text-teal-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'call_off' ? 'text-teal-700' : 'text-slate-600'}`}>Call Off</span>
                  </div>
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          )}
        </CardHeader>
      </Card>

      {/* Tab Content.
          CR-7058: every panel is guarded by its own visibility flag as well as
          by getValidTab, so a ?tab=... deep link can never render a surface
          whose trigger the tenant has switched off. Nothing renders until
          tabsReady, so the panel cannot flash on screen while the settings
          are still loading. */}
      {tabsReady && (
        <>
          {isTabVisible('rfq') && mainTab === 'rfq' && (
            <Suspense fallback={<div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <RFQTab />
            </Suspense>
          )}

          {pricingTabVisible && mainTab === 'contract_pricing' && <ContractPricingTab />}

          {isTabVisible('po_headers') && mainTab === 'po_headers' && renderPOHeadersContent()}

          {isTabVisible('po_lines') && mainTab === 'po_lines' && <POLinesTab initialLineId={urlLine ? parseInt(urlLine, 10) : null} initialLineTab={urlLineTab || undefined} />}

          {tabsReady && !isTabVisible('po_lines') && <LineDeepLinkModal />}

          {mainTab === 'schedules' && showSchedules && <POLineSchedules />}

          {mainTab === 'forecast' && forecastTabVisible && <ForecastTab />}

          {isTabVisible('late_to_raise') && mainTab === 'late_to_raise' && <LateToRaiseTab />}

          {isTabVisible('asn') && mainTab === 'asn' && <ASNTab />}

          {isTabVisible('delivery') && mainTab === 'delivery' && <DeliveryTab />}

          {showCallOff && mainTab === 'call_off' && <CallOffTab />}
        </>
      )}

      {/* PO Detail Modal */}
      <PODetailModal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        poId={detailModal.data || 0}
        initialTab={detailInitialTab}
      />
    </div>
  );
}
